import React from 'react';
import { ArrowRight, Trash2 } from 'lucide-react';
import { formatEuro, type Board } from './types';

export interface BoardCardStats {
    covers: number;
    encaisse: number;
    reste: number;
    /** Nombre de réservations importées d'une plateforme — masqué s'il vaut zéro. */
    imported: number;
    reservationCount: number;
}

/**
 * Une carte de service dans l'écran d'année (maquette 16a).
 *
 * La pastille calendrier porte la date du SERVICE, pas celle de la création : c'est par elle qu'on
 * repère un service dans une année, et c'est la seule information qu'on lit vraiment de loin.
 */
export function BoardCard({ board, stats, isNext, onOpen, onDuplicate, onArchive, onDelete }: {
    board: Board;
    stats?: BoardCardStats;
    /** Prochaine date à venir : la carte se distingue par une bordure franche et une pastille inversée. */
    isNext: boolean;
    onOpen: () => void;
    onDuplicate: () => void;
    onArchive: () => void;
    onDelete: () => void;
}) {
    const date = board.serviceDate ? new Date(`${board.serviceDate}T00:00:00`) : null;
    const month = date ? date.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '') : '—';
    const day = date ? date.getDate() : '—';
    const weekday = date ? date.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '') : '';

    const reste = stats?.reste ?? 0;
    const hasReservations = (stats?.reservationCount ?? 0) > 0;

    return (
        <div className={`flex flex-col p-4 rounded-xl bg-surface transition-transform hover:-translate-y-px ${
            isNext ? 'border-2 border-content' : 'border border-border'
        }`}>
            <div className="flex items-start gap-3 mb-3.5">
                <div className={`w-[52px] shrink-0 py-1.5 rounded-[10px] text-center border ${
                    isNext ? 'bg-ink border-ink' : 'bg-surface-soft border-border'
                }`}>
                    <div className={`text-[9px] font-bold uppercase tracking-[.07em] ${isNext ? 'text-white/70' : 'text-content-muted'}`}>{month}</div>
                    <div className={`text-[19px] font-bold leading-tight tabular-nums ${isNext ? 'text-white' : 'text-content'}`}>{day}</div>
                    <div className={`text-[9px] font-semibold ${isNext ? 'text-white/70' : 'text-content-muted'}`}>{weekday}</div>
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="text-[15px] font-bold text-content truncate mb-1">{board.title}</h2>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {/* « Aucune réservation » plutôt qu'un « 0 couvert » : un compteur à zéro se lit comme
                            une erreur de calcul, la phrase dit que le tableau est simplement vide. */}
                        <span className="text-[11.5px] text-content-muted tabular-nums whitespace-nowrap">
                            {hasReservations ? `${stats!.covers} couverts` : 'Aucune réservation'}
                        </span>
                        {!!stats?.imported && (
                            <span className="inline-flex items-center gap-1 h-[19px] px-1.5 rounded-full bg-state-info-soft border border-state-info/30 text-state-info text-[9.5px] font-bold shrink-0">
                                <span className="w-1 h-1 rounded-full bg-state-info" />{stats.imported} importées
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-1.5 mb-3.5">
                <Tile label="Encaissé" value={formatEuro(stats?.encaisse ?? 0)} tone="ok" />
                <Tile label="Reste" value={formatEuro(reste)} tone={reste > 0 ? 'warn' : 'neutral'} />
            </div>

            <div className="flex items-center justify-between gap-2.5 flex-wrap mt-auto">
                <button onClick={onOpen}
                    className="h-8 px-3.5 inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-xs font-semibold hover:opacity-90 transition-opacity shrink-0">
                    Ouvrir <ArrowRight size={12} />
                </button>
                <div className="flex items-center gap-0.5 shrink-0">
                    <button onClick={onDuplicate} className="h-7 px-2.5 rounded-full text-[11.5px] font-semibold text-content-muted hover:bg-surface-soft hover:text-content transition-colors">
                        Dupliquer
                    </button>
                    <button onClick={onArchive} className="h-7 px-2.5 rounded-full text-[11.5px] font-semibold text-content-muted hover:bg-surface-soft hover:text-content transition-colors">
                        Archiver
                    </button>
                    <button onClick={onDelete} title="Supprimer le tableau" aria-label="Supprimer le tableau"
                        className="h-7 w-7 inline-flex items-center justify-center rounded-full text-content-muted hover:bg-state-danger-soft hover:text-state-danger transition-colors">
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function Tile({ label, value, tone }: { label: string; value: string; tone: 'ok' | 'warn' | 'neutral' }) {
    const tones = {
        ok: 'bg-state-ok-soft border-state-ok/25 text-state-ok',
        warn: 'bg-state-warn-soft border-state-warn/25 text-state-warn',
        neutral: 'bg-surface-soft border-border-soft text-content',
    } as const;
    return (
        <div className={`px-2.5 py-2 rounded-[9px] border ${tones[tone]}`}>
            <div className="text-[9px] font-bold uppercase tracking-[.07em] opacity-80 mb-0.5">{label}</div>
            <div className="text-[13.5px] font-bold tabular-nums whitespace-nowrap">{value}</div>
        </div>
    );
}
